/**
 * Whisper QA for support-uploaded phone call recordings (OrderRemarksFile).
 * Lists recordings from /geo/playground/call-recordings and transcribes on demand.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Headphones,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { geoGet, geoPost } from '@/api/geo/client';
import { cn } from '@/lib/cn';
import { fmt } from '@/lib/format';

export interface CallRecording {
  id: number;
  order_id: number | null;
  name: string | null;
  phone: string | null;
  role: string | null;
  duration: number | null;
  size: number | null;
  is_outgoing: boolean;
  called_at: string | null;
  created_at: string | null;
  file: string;
  media_url: string;
  uploader_name: string | null;
}

interface CallRecordingsResponse {
  recordings: CallRecording[];
  total: number;
  limit: number;
  offset: number;
}

interface TranscribeResponse {
  recording_id: number;
  transcript: string;
  model: string;
  filename: string;
  bytes: number;
}

function resolveMediaUrl(file: string | null | undefined): string | null {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return file;
  const base = import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return `${base}${file.startsWith('/') ? '' : '/'}${file}`;
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function whenLabel(rec: CallRecording): string {
  const raw = rec.called_at || rec.created_at;
  if (!raw) return '—';
  return fmt.datetime(raw);
}

interface CallTranscriptionSectionProps {
  from: string;
  to: string;
  rangeValid: boolean;
  endExclusive: (to: string) => string;
}

export function CallTranscriptionSection({
  from,
  to,
  rangeValid,
  endExclusive,
}: CallTranscriptionSectionProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [transcriptById, setTranscriptById] = useState<Record<number, TranscribeResponse>>({});

  const queryKey = useMemo(
    () => ['playground-call-recordings', from, to] as const,
    [from, to],
  );

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (rangeValid) {
        params.set('start', from);
        params.set('end', endExclusive(to));
      }
      params.set('limit', '100');
      const qs = params.toString();
      return geoGet<CallRecordingsResponse>(
        `/playground/call-recordings${qs ? `?${qs}` : ''}`,
      );
    },
    enabled: rangeValid,
    staleTime: 60_000,
  });

  const recordings = data?.recordings ?? [];
  const selected = recordings.find((r) => r.id === selectedId) ?? null;

  useEffect(() => {
    if (selectedId != null && !recordings.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [recordings, selectedId]);

  const transcribe = useMutation({
    mutationFn: (id: number) =>
      geoPost<TranscribeResponse>(
        `/playground/call-recordings/${id}/transcribe`,
        undefined,
        { timeout: 180_000 },
      ),
    onSuccess: (res) => {
      setTranscriptById((prev) => ({ ...prev, [res.recording_id]: res }));
    },
  });

  const audioUrl = selected ? resolveMediaUrl(selected.media_url) : null;
  const activeTranscript = selected ? transcriptById[selected.id] : undefined;
  const transcribeError =
    transcribe.isError && selected && transcribe.variables === selected.id
      ? (transcribe.error as { response?: { data?: { detail?: string } }; message?: string })
      : null;
  const errorDetail =
    transcribeError?.response?.data?.detail ||
    transcribeError?.message ||
    (transcribeError ? 'Transcription failed.' : null);

  return (
    <section className="mt-10 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Call transcription</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            Select a support-uploaded phone call recording and run Whisper to review transcript
            quality. Uses the page date range above. Transcripts are not saved.
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          disabled={!rangeValid || isFetching}
          onClick={() => void refetch()}
        >
          {isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          )}
          Refresh
        </Button>
      </div>

      {!rangeValid ? (
        <p className="text-xs text-muted-foreground">Choose a valid From / To date range.</p>
      ) : isError ? (
        <ErrorState title="Failed to load call recordings" onRetry={() => void refetch()} />
      ) : isLoading || !data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-72 rounded-lg" />
          <Skeleton className="h-72 rounded-lg" />
        </div>
      ) : recordings.length === 0 ? (
        <Card>
          <CardBody className="py-8 text-center text-xs text-muted-foreground">
            No call recordings with audio in this date range.
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="overflow-hidden">
            <CardHeader className="border-b border-border/60">
              <CardTitle className="flex items-center gap-2 text-[13px]">
                <Headphones className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                Recordings
                <span className="font-normal text-muted-foreground">
                  ({data.total.toLocaleString()}
                  {data.total > recordings.length ? `, showing ${recordings.length}` : ''})
                </span>
              </CardTitle>
            </CardHeader>
            <CardBody className="max-h-[28rem] space-y-0 overflow-y-auto p-0">
              <ul className="divide-y divide-border/60">
                {recordings.map((rec) => {
                  const active = rec.id === selectedId;
                  const DirIcon = rec.is_outgoing ? PhoneOutgoing : PhoneIncoming;
                  return (
                    <li key={rec.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedId(rec.id);
                          transcribe.reset();
                        }}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                          active ? 'bg-muted/60' : 'hover:bg-muted/35',
                        )}
                      >
                        <DirIcon
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
                          aria-hidden
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-xs font-medium text-foreground">
                              {rec.name || rec.phone || 'Unknown contact'}
                            </span>
                            {rec.role ? (
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                {rec.role}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                            <span>{whenLabel(rec)}</span>
                            {rec.order_id != null ? <span>Order #{rec.order_id}</span> : null}
                            <span>{formatDuration(rec.duration)}</span>
                            <span>{formatBytes(rec.size)}</span>
                            {rec.uploader_name ? <span>{rec.uploader_name}</span> : null}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="border-b border-border/60">
              <CardTitle className="text-[13px]">
                {selected ? `Recording #${selected.id}` : 'Select a recording'}
              </CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col gap-4">
              {!selected ? (
                <p className="py-10 text-center text-xs text-muted-foreground">
                  Choose a row from the list to play audio and run Whisper.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    <span>
                      Contact{' '}
                      <span className="font-medium text-foreground">
                        {selected.name || '—'}
                        {selected.phone ? ` · ${selected.phone}` : ''}
                      </span>
                    </span>
                    <span>
                      Duration{' '}
                      <span className="font-medium text-foreground">
                        {formatDuration(selected.duration)}
                      </span>
                    </span>
                    <span>
                      Size{' '}
                      <span className="font-medium text-foreground">
                        {formatBytes(selected.size)}
                      </span>
                    </span>
                  </div>

                  {audioUrl ? (
                    <audio controls preload="metadata" src={audioUrl} className="w-full" />
                  ) : (
                    <p className="text-xs text-muted-foreground">No playable media URL.</p>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={transcribe.isPending && transcribe.variables === selected.id}
                      onClick={() => transcribe.mutate(selected.id)}
                    >
                      {transcribe.isPending && transcribe.variables === selected.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" aria-hidden />
                      )}
                      {activeTranscript ? 'Re-transcribe' : 'Transcribe'}
                    </Button>
                    {activeTranscript ? (
                      <span className="text-[11px] text-muted-foreground">
                        model {activeTranscript.model} ·{' '}
                        {(activeTranscript.bytes / 1024).toFixed(1)} KB
                      </span>
                    ) : null}
                  </div>

                  {errorDetail ? (
                    <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                      {errorDetail}
                    </p>
                  ) : null}

                  {activeTranscript ? (
                    <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/30 p-3 font-mono text-[12px] leading-relaxed text-foreground">
                      {activeTranscript.transcript || '(empty transcript)'}
                    </pre>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Listen to the call, then transcribe to compare quality.
                    </p>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </section>
  );
}
